/**
 * Source manager — manages registration, lifecycle, and status of all data sources.
 * Used by the scheduler and pipeline.
 */

import type { Source, SourceManager, SourceCategory, SourceStatus } from './base-source';

class SourceManagerImpl implements SourceManager {
  private sources = new Map<string, Source>();

  register(source: Source): void {
    if (this.sources.has(source.id)) {
      console.warn(`[Sentinel] Source "${source.id}" already registered, replacing`);
    }
    this.sources.set(source.id, source);
  }

  unregister(sourceId: string): void {
    const source = this.sources.get(sourceId);
    if (source) {
      source.stop().catch(err => {
        console.error(`[Sentinel] Error stopping source "${sourceId}":`, err);
      });
      this.sources.delete(sourceId);
    }
  }

  getAll(): Source[] {
    return Array.from(this.sources.values());
  }

  getByCategory(category: SourceCategory): Source[] {
    return this.getAll().filter(s => s.meta.category === category);
  }

  async startAll(): Promise<void> {
    const starts = this.getAll().map(async source => {
      try {
        await source.start();
      } catch (err) {
        console.error(`[Sentinel] Failed to start source "${source.id}":`, err);
      }
    });
    await Promise.allSettled(starts);
  }

  async stopAll(): Promise<void> {
    const stops = this.getAll().map(async source => {
      try {
        await source.stop();
      } catch (err) {
        console.error(`[Sentinel] Failed to stop source "${source.id}":`, err);
      }
    });
    await Promise.allSettled(stops);
  }

  getStatus(): Map<string, SourceStatus> {
    const statuses = new Map<string, SourceStatus>();
    for (const [id, source] of this.sources) {
      statuses.set(id, source.getStatus());
    }
    return statuses;
  }
}

export const sourceManager = new SourceManagerImpl();
